import type { DailyCheckinClaimErrorKind } from '~/types/daily-checkin';

// Erro tipado do resgate: o adapter só CLASSIFICA a falha; a mensagem pro
// usuário fica na store, decidida pelo kind — nunca casando string de copy
// entre camadas. Mesmo desenho do RouletteSpinError.
export class DailyCheckinClaimError extends Error {
  constructor(
    public readonly kind: DailyCheckinClaimErrorKind,
    message?: string,
  ) {
    super(message ?? kind);
    this.name = 'DailyCheckinClaimError';
  }
}

// Códigos de recusa que o core devolve em `error` no corpo do 4xx/5xx
// (ENG-425). Qualquer código fora desta lista cai em 'api', que mostra uma
// mensagem genérica — nome novo no core degrada sem quebrar a tela.
const ERROR_KINDS: Record<string, DailyCheckinClaimErrorKind> = {
  unauthenticated: 'unauthenticated',
  no_active_period: 'no_active_period',
  check_in_disabled: 'check_in_disabled',
  not_eligible: 'not_eligible',
  already_claimed_today: 'already_claimed_today',
  track_completed: 'track_completed',
  delivery_failed: 'delivery_failed',
  delivery_uncertain: 'delivery_uncertain',
  step_unavailable: 'step_unavailable',
};

export function classifyClaimError(
  status: number,
  code: unknown,
): DailyCheckinClaimErrorKind {
  // Payload recusado é a ÚNICA falha que não fala a língua do contrato: cai na
  // validação padrão do Laravel, que devolve 422 com { message, errors } e
  // nenhuma chave `error`. Por isso vem antes do lookup — sem essa linha o caso
  // despencaria em 'api', com copy genérica e fora da lista que sobe pro Sentry,
  // justamente no erro que é bug nosso e não do jogador.
  //
  // Antes o único jeito de chegar aqui era um `user_id` recusado. Sem ele
  // (ENG-801) o resgate manda corpo vazio, então um 422 passa a ser descompasso
  // de contrato: o core espera algo que o front não manda. Mais raro, e mais
  // grave — por isso continua no Sentry.
  if (status === 422) return 'invalid_request';

  if (typeof code === 'string' && ERROR_KINDS[code]) {
    return ERROR_KINDS[code];
  }

  // O 429 é o único do contrato que NÃO se nomeia no corpo, então cai no status.
  if (status === 429) return 'rate_limited';
  if (status === 401) return 'unauthenticated';

  return 'api';
}
